import type { Nullish } from '@iheartjane/types';

import { useBloomBasename } from './useBloomBasename';
import { useMixpanelAnalyticsTags } from './useMixpanelAnalyticsTags';

/**
 * Hook to generate checkout URLs with analytics tags and promo code.
 * @param {object} options - Configuration options
 * @param {boolean} options.includeBasename - Whether to include the basename in the URL (default: true)
 * @param {Nullish<string>} options.promoCode - Optional promo code to include in the URL
 * @returns {string} The checkout URL with analytics tags and promo code if available
 * @example
 * // For Button components that need full URLs
 * const { promoCode } = useRootLoaderData();
 * const checkoutUrl = useCheckoutLink({ promoCode });
 *
 * // For LinkWithBasename components that handle basename automatically
 * const { promoCode } = useRootLoaderData();
 * const checkoutUrl = useCheckoutLink({ includeBasename: false, promoCode });
 *
 * // Without any options
 * const checkoutUrl = useCheckoutLink();
 */
export const useCheckoutLink = (
  options: { includeBasename?: boolean; promoCode?: Nullish<string> } = {}
): string => {
  const { includeBasename = true, promoCode } = options;
  const basename = useBloomBasename();
  const analyticsTags = useMixpanelAnalyticsTags();

  const baseUrl = includeBasename ? `${basename}checkout` : '/checkout';

  const params = new URLSearchParams(analyticsTags || '');
  if (promoCode) {
    params.set('promo_code', promoCode);
  }

  return params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;
};
